import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X, AlertTriangle } from 'lucide-react';

interface BoxRejectModalProps {
  isOpen: boolean;
  onClose: () => void;
  onReject: (reason: string) => void;
  orderNumber: string;
}

const BoxRejectModal: React.FC<BoxRejectModalProps> = ({
  isOpen,
  onClose,
  onReject,
  orderNumber
}) => {
  const { t } = useTranslation(['orders', 'common']);
  const [selectedReason, setSelectedReason] = useState<string>('');

  // BOX rejection reasons with their codes
  const rejectionReasons = [
    { code: 'high_load', label: t('boxReject.highLoad') },
    { code: 'no_driver', label: t('boxReject.noDriver') },
    { code: 'product_unavailable', label: t('boxReject.productUnavailable') },
    { code: 'out_of_area', label: t('boxReject.outOfArea') },
    { code: 'wrong_price', label: t('boxReject.wrongPrice') },
    { code: 'closing_soon', label: t('boxReject.closingSoon') },
    { code: 'bad_weather', label: t('boxReject.badWeather') }
  ];

  const handleReject = () => {
    if (selectedReason) {
      onReject(selectedReason);
      setSelectedReason('');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="cashier-overlay fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="cashier-dialog bg-surface-1 rounded-xl shadow-2xl border border-line w-full max-w-md" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="cashier-dialog-header flex items-center justify-between px-5 py-4 border-b border-line">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-red-500/20 flex items-center justify-center">
              <AlertTriangle className="h-5 w-5 text-red-600 dark:text-red-400" />
            </div>
            <div>
              <h2 className="text-base font-bold text-t1">{t('boxReject.title', { orderNumber })}</h2>
              <p className="text-xs text-t3">{t('boxReject.selectReason')}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg text-t3 hover:text-t1 hover:bg-surface-2 transition-colors">
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Reason List */}
        <div className="p-4 space-y-1.5 max-h-[60vh] overflow-y-auto">
          {rejectionReasons.map((reason) => (
            <button
              key={reason.code}
              onClick={() => setSelectedReason(reason.code)}
              className={`w-full text-left px-4 py-3 rounded-lg border transition-all text-sm font-medium ${
                selectedReason === reason.code
                  ? 'border-red-500 bg-red-500/15 text-red-700 dark:text-red-300'
                  : 'border-line-strong bg-surface-2 dark:bg-surface-2/50 text-t2 hover:border-line-strong hover:bg-surface-2'
              }`}
            >
              {reason.label}
            </button>
          ))}
        </div>

        {/* Footer */}
        <div className="px-4 py-3 border-t border-line flex justify-end gap-2">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-t2 hover:text-t1 bg-surface-2 hover:bg-surface-3 rounded-lg transition-colors"
          >
            {t('common:cancel')}
          </button>
          <button
            onClick={handleReject}
            disabled={!selectedReason}
            className={`px-5 py-2 rounded-lg text-sm font-semibold transition-all ${
              selectedReason
                ? 'bg-red-600 text-white hover:bg-red-700 active:scale-95'
                : 'bg-surface-3 text-t3 cursor-not-allowed'
            }`}
          >
            {t('reject')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default BoxRejectModal;
